import React, { useMemo, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ROUTES from 'constants/routes'
import { USER_TYPES } from 'constants/users'
import { shouldShowPostLoginOnboardingSelector } from 'store/user/selectors'
import useOnboardingTour from 'hooks/useOnboardingTour'
import { buildGameSelectionSteps, buildLobbySteps } from './onboardingSteps'

const SESSION_STORAGE_PREFIX = 'post-login-onboarding-done:'

const buildSessionKey = ({ track, userType, userId }) => {
  if (!userType || !userId) return null
  return `${SESSION_STORAGE_PREFIX}${userType}:${userId}:${track}`
}

const hasCompletedTrackInSession = (sessionKey) => {
  if (!sessionKey || typeof window === 'undefined') return false
  return window.sessionStorage.getItem(sessionKey) === 'true'
}

const markTrackCompletedInSession = (sessionKey) => {
  if (!sessionKey || typeof window === 'undefined') return
  window.sessionStorage.setItem(sessionKey, 'true')
}

const clearOnboardingSessionFlags = () => {
  if (typeof window === 'undefined') return

  Object.keys(window.sessionStorage).forEach((key) => {
    if (key.startsWith(SESSION_STORAGE_PREFIX)) {
      window.sessionStorage.removeItem(key)
    }
  })
}

/**
 * Orchestrates the post-login onboarding tour across screens.
 *
 * Visibility is tied to games_entered_count < GAMES_ENTERED_FOR_ONBOARDING
 * (see shouldShowPostLoginOnboardingSelector).
 *
 * - On /game-selection: Track 1 (Create vs Join).
 * - On /lobby: Track 2 (lobby walkthrough).
 * Dismissing the tour does not change backend state or session state, so it can
 * reappear on refresh. Completing a track via "Done" hides that track until the
 * next login while the user still has fewer than 2 started games.
 */
function PostLoginOnboardingOrchestrator() {
  const { t } = useTranslation()
  const location = useLocation()
  const loggedIn = useSelector((state) => state.auth.loggedIn)
  const shouldShow = useSelector(shouldShowPostLoginOnboardingSelector)
  const userId = useSelector((state) => state.user.info.data?.id)
  const userType = useSelector((state) => state.user.info.data?.user_type)
  const gameState = useSelector((state) => state.auth.gameInfo?.aasm_state)
  const isCreator = useSelector((state) => Boolean(state.auth.lastCreatedGame?.is_creator))

  const onGameSelection = location.pathname === ROUTES.APP_URL_GAME_SELECTION
  const onLobby = Boolean(useRouteMatch({ path: ROUTES.APP_URL_LOBBY, exact: false }))
  const isFacilitatorObserver = userType === USER_TYPES.facilitator && gameState === 'started'

  const gameSelectionSteps = useMemo(() => buildGameSelectionSteps(t), [t])
  const lobbySteps = useMemo(() => buildLobbySteps(t, { isCreator }), [t, isCreator])
  const gameSelectionSessionKey = useMemo(
    () => buildSessionKey({ track: 'game-selection', userType, userId }),
    [userType, userId]
  )
  const lobbySessionKey = useMemo(
    () => buildSessionKey({ track: 'lobby', userType, userId }),
    [userType, userId]
  )
  const gameSelectionCompletedInSession = useMemo(
    () => hasCompletedTrackInSession(gameSelectionSessionKey),
    [gameSelectionSessionKey]
  )
  const lobbyCompletedInSession = useMemo(
    () => hasCompletedTrackInSession(lobbySessionKey),
    [lobbySessionKey]
  )

  useEffect(() => {
    if (!loggedIn) clearOnboardingSessionFlags()
  }, [loggedIn])

  const handleGameSelectionDone = useCallback(() => {
    markTrackCompletedInSession(gameSelectionSessionKey)
  }, [gameSelectionSessionKey])

  const handleLobbyDone = useCallback(() => {
    markTrackCompletedInSession(lobbySessionKey)
  }, [lobbySessionKey])

  const noop = useCallback(() => {}, [])

  useOnboardingTour({
    steps: gameSelectionSteps,
    active: shouldShow && onGameSelection && !gameSelectionCompletedInSession,
    onDone: handleGameSelectionDone,
    onDismiss: noop
  })

  useOnboardingTour({
    steps: lobbySteps,
    // TODO(GAM-268): facilitator observers (facilitator + started game) are
    // intentionally excluded from onboarding for now; add a dedicated observer
    // tour later if product wants it.
    active: shouldShow && onLobby && !isFacilitatorObserver && !lobbyCompletedInSession,
    onDone: handleLobbyDone,
    onDismiss: noop
  })

  return null
}

export default PostLoginOnboardingOrchestrator
